// Represents length, width, or thickness.

import { seededRandom } from "../utils/randUtils";
import { Tuple } from "../utils/typeUtils";
import type { ToolId } from "./Tool";
import { PRODUCT_PLAN_IDS, ProductPlanId } from "./plans";

/**
 * The cross-section detents: width in inches, thickness in quarters of an
 * inch. Lengths are NOT on this scale — a length is a plain number of
 * inches, because cuts do arithmetic on lengths (cutBoard subtracts) and
 * recipes want off-grid values (a 34" crate wall, a 7" birdhouse side).
 */
export const BOARD_DIMENSIONS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export const SHEET_THICKNESSES = [1, 2, 3, 4] as const; // in quarters of an inch
export type BoardDimension = (typeof BOARD_DIMENSIONS)[number];
export type SheetThickness = (typeof SHEET_THICKNESSES)[number];

export const SPECIES = [
  "pallet",
  "pine",
  "poplar",
  "oak",
  "maple",
  "cherry",
  "walnut",
  "mahogany",
  "purpleHeart",
] as const;

export type Species = (typeof SPECIES)[number];

/** Every species except reclaimed pallet wood — the ones fit for real work. */
export const REAL_WOOD_SPECIES: ReadonlyArray<Species> = SPECIES.filter(
  (species) => species !== "pallet",
);

/**
 * Surface quality ladder (see docs/tools-and-surfaces.md). Sanding bumps a
 * material one step up; planing produces "smooth" (never "sanded"); glue-ups
 * always come out "rough". Finish quality only — geometry (flat, straight)
 * lives on the jointed axes below. Sanding never flattens anything.
 */
export const SURFACE_CONDITIONS = ["rough", "smooth", "sanded"] as const;
export type SurfaceCondition = (typeof SURFACE_CONDITIONS)[number];

/**
 * How many of a board's faces (or edges) are jointed true. Milling is two
 * independent axes, not a ladder: after a reference face and edge exist,
 * planing (faces 2) and ripping (edges 2) can happen in either order.
 *
 * Faces: 0 = rough/possibly warped, 1 = one flat reference face,
 * 2 = faces parallel ("planed"). Ends are never tracked — crosscuts have no
 * prerequisites. Milling never consumes nominal dimension: "rough" stock
 * carries sacrificial material beyond its listed size.
 */
export type JointedCount = 0 | 1 | 2;

/**
 * The magnitudes of a saw's angle stops, measured off square — 0° is a
 * plain crosscut. 45° makes rectangular frames; 30° and 22.5° are the
 * hexagon and octagon stops.
 */
export const MITER_ANGLES = [22.5, 30, 45] as const;
export type MiterAngle = (typeof MITER_ANGLES)[number];

/**
 * A mitered end's angle is SIGNED — a real saw head swings both ways off
 * square, and the sign is what distinguishes the two shapes two 45° ends
 * can make. Convention: both ends are measured with the same rotational
 * sense (the angle of the cut line off the width axis, board laid along
 * x), so ends with EQUAL angles are parallel (a parallelogram) and ends
 * with OPPOSITE angles mirror (a frame rail). Because a faceless board
 * can be flipped over — which negates both ends at once — anything
 * comparing ends must compare their relative sign, never absolute.
 */
export type SignedMiterAngle = MiterAngle | -22.5 | -30 | -45;

/**
 * What one end of a board looks like. A discriminated union so future end
 * features (tenons, dowel holes) slot in as new kinds; per-end state exists
 * because advanced work cares WHICH end carries the treatment.
 */
export type BoardEnd =
  | { readonly kind: "square" }
  | { readonly kind: "mitered"; readonly angle: SignedMiterAngle };

export interface BoardEnds {
  readonly left: BoardEnd;
  readonly right: BoardEnd;
}

export const SQUARE_END: BoardEnd = { kind: "square" };

/** The next step up the surface ladder, or null at the top. */
export function improvedSurface(
  surface: SurfaceCondition,
): SurfaceCondition | null {
  const index = SURFACE_CONDITIONS.indexOf(surface);
  return SURFACE_CONDITIONS[index + 1] ?? null;
}

/**
 * The rougher of two conditions. A board carries one surface for the whole
 * piece, so an operation that leaves one face worse than the rest (a resaw
 * opens a fresh sawn face) reports the worst face — the one that still
 * needs work. Per-face surface tracking would let both be remembered.
 */
export function worseSurface(
  a: SurfaceCondition,
  b: SurfaceCondition,
): SurfaceCondition {
  return SURFACE_CONDITIONS.indexOf(a) <= SURFACE_CONDITIONS.indexOf(b) ? a : b;
}

/**
 * The rectangle of wood a board-face scan represents: the boards' art is
 * a library of full-board photographs, each standing for one canonical
 * 8-foot, 8-inch-wide plank. Face regions are measured in inches on this
 * rectangle, and a virgin board is placed somewhere inside it
 * (defaultBoardFace in board-helpers.ts).
 */
export const BOARD_FACE_SCAN_LENGTH_IN = 96;
export const BOARD_FACE_SCAN_WIDTH_IN = 8;

/**
 * Where a board's face lies on the virgin board it was cut from —
 * cosmetic provenance for the renderer, the boards' version of
 * SheetFaceRegion. Simpler than the sheets': a board's length is along
 * the grain by definition, so no cut ever turns one and there is no
 * rotation to remember. `u` runs along the source board from its left
 * end (the end the sprite draws at its top), `v` across its width, both
 * inches on the canonical scan rectangle above. Purely visual — no cut,
 * recipe, or price reads it.
 */
export interface BoardFaceRegion {
  /** Identity of the virgin source board — picks which scan of the
   * species' library it shows, and seeds where on it the source sat. */
  readonly seed: string;
  readonly u: number;
  readonly v: number;
}

/**
 * A virgin board's face region: the board placed at a seeded spot inside
 * the canonical scan rectangle, so boards sharing one scan don't all
 * show the same stretch of plank. A full-size board fills the scan and
 * sits at its origin.
 *
 * Called by makeMaterial the moment a board is minted, because the
 * placement is seeded by the board's first id: operations that transform
 * a board in place (planing, jointing) re-mint it under a fresh id, and
 * a placement left implicit would reroll the very grain those
 * operations claim to reveal.
 */
export function defaultBoardFace(
  seed: string,
  length: number,
  width: number,
): BoardFaceRegion {
  const rng = seededRandom(seed);
  return {
    seed,
    u: rng() * Math.max(0, BOARD_FACE_SCAN_LENGTH_IN - length),
    v: rng() * Math.max(0, BOARD_FACE_SCAN_WIDTH_IN - width),
  };
}

export interface Board {
  readonly id: string;
  readonly type: "board";
  /** Inches. Integer by convention (saws cut at inch marks). */
  readonly length: number;
  readonly width: BoardDimension;
  readonly thickness: BoardDimension;
  readonly species: Species;
  readonly surface: SurfaceCondition;
  /** Flat faces: planing requires 1 (a reference face) and produces 2. */
  readonly jointedFaces: JointedCount;
  /** Straight edges: ripping and gluing require them (see board-helpers). */
  readonly jointedEdges: JointedCount;
  /**
   * End treatments, left and right as the board lies on the saw. Absent
   * means both ends square (pre-miter saves and untouched stock — the
   * Panel.grain precedent). Length cuts rewrite these (see cutBoard).
   */
  readonly ends?: BoardEnds;
  /** Absent on a virgin board — it gets a seeded placement on the scan
   * (see boardFaceRegion). Cuts fill it in. */
  readonly face?: BoardFaceRegion;
}

/** A board's end state with the square/square default applied. */
export function boardEnds(board: Board): BoardEnds {
  return board.ends ?? { left: SQUARE_END, right: SQUARE_END };
}

/**
 * Short label for a board's end treatments, or null when both ends are the
 * unremarkable square default. Reads like a cut list: "45° both ends" is
 * the mirrored (frame-rail) pair; equal-signed ends — the same magnitude
 * leaning the same way — read "parallel ends". A lone miter's sign is
 * meaningless on a faceless board, so single ends show the magnitude.
 */
export function endsLabel(board: Board): string | null {
  const { left, right } = boardEnds(board);
  if (left.kind === "mitered" && right.kind === "mitered") {
    if (left.angle === -right.angle) {
      return `${Math.abs(left.angle)}° both ends`;
    }
    if (left.angle === right.angle) {
      return `${Math.abs(left.angle)}° parallel ends`;
    }
    return `${left.angle}°/${right.angle}° ends`;
  }
  const mitered = left.kind === "mitered" ? left : right;
  return mitered.kind === "mitered"
    ? `${Math.abs(mitered.angle)}° one end`
    : null;
}

/**
 * How far each mitered end line runs along the board, in inches: the
 * end's SIGNED angle across the board's width, so both end lines lean
 * the same way for the same angle — equal angles draw parallel (a
 * parallelogram), opposite angles the mirror pair that closes a frame
 * corner. Clamped so two steep miters on a short, wide board can't
 * cross. Zero for a square end.
 */
export function miterSkews(
  widthIn: number,
  lengthIn: number,
  ends: BoardEnds | undefined,
): { readonly left: number; readonly right: number } {
  const skewFor = (end: BoardEnd | undefined): number => {
    if (end?.kind !== "mitered") return 0;
    const run = widthIn * Math.tan((end.angle * Math.PI) / 180);
    return Math.sign(run) * Math.min(Math.abs(run), lengthIn * 0.45);
  };
  return { left: skewFor(ends?.left), right: skewFor(ends?.right) };
}

/**
 * A board's face as seen from above, in its own frame — width across
 * (x), length along (y), the left end at −y — with its mitered ends cut
 * in: four corners, the left end's two first. A positive skew pulls the
 * +x corner back at the left end and the −x corner at the right end,
 * so the outline is the shape the finished piece draws and the shape
 * a plan lays out, from one place.
 */
export function boardFaceOutline(
  widthIn: number,
  lengthIn: number,
  ends: BoardEnds | undefined,
): ReadonlyArray<{ readonly x: number; readonly y: number }> {
  const { left, right } = miterSkews(widthIn, lengthIn, ends);
  const w = widthIn / 2;
  const h = lengthIn / 2;
  return [
    { x: -w, y: -h + Math.max(0, left) },
    { x: w, y: -h + Math.max(0, -left) },
    { x: w, y: h - Math.max(0, right) },
    { x: -w, y: h - Math.max(0, -right) },
  ];
}

/**
 * Short label for a board's milled state, or null when it's the unremarkable
 * default (a flat-enough, straight-edged board — pallet stock and any board
 * from before the milling system).
 */
export function millingLabel(board: Board): string | null {
  const { jointedFaces: faces, jointedEdges: edges } = board;
  if (faces === 2) {
    return edges === 2 ? "S4S" : edges === 1 ? "S3S" : "S2S";
  }
  if (faces === 0) {
    return "rough sawn";
  }
  return edges === 2 ? null : "face jointed";
}

export const SHEET_GOOD_KINDS = [
  "plywoodA", // high quality
  "plywoodB", // medium quality
  "plywoodC", // low quality
  "mdf",
  "osb",
  "particleBoard",
] as const;

export type SheetGoodKind = (typeof SHEET_GOOD_KINDS)[number];

/**
 * Pseudo-species for the sheet goods, so their dust has somewhere to go
 * (see docs/dust-and-cleaning.md). Sheets have no wood species — a
 * plywood sheet is a stack of whatever the mill had — so what they shed
 * is tracked by the sheet family instead: gluey plywood chips, choking
 * MDF powder, the resinous grit off a chip board.
 */
export const SHEET_DUST_SPECIES = [
  "plywood",
  "mdf",
  "osb",
  "particleBoard",
] as const;

export type SheetDustSpecies = (typeof SHEET_DUST_SPECIES)[number];

/**
 * Everything that can land on the floor as dust: the real woods plus the
 * sheet pseudo-species. `GameState.dust`, the dustpan, and the vac
 * canister are all keyed by this.
 */
export const DUST_SPECIES = [...SPECIES, ...SHEET_DUST_SPECIES] as const;

export type DustSpecies = (typeof DUST_SPECIES)[number];

/** Which pseudo-species a sheet sheds — the three plywood grades all
 * make the same mess. */
export function sheetDustSpecies(kind: SheetGoodKind): SheetDustSpecies {
  switch (kind) {
    case "mdf":
    case "osb":
    case "particleBoard":
      return kind;
    default:
      return "plywood";
  }
}

/**
 * Which sheet kinds a recipe accepts is a statement about the work, not
 * the wood budget. A jig base must be flat and hold runner screws — any
 * plywood or MDF, never the chip boards, which sag and crumble around
 * screws. Shop furniture is less picky: particle board tops a worktable
 * honestly, but OSB's lumpy face is fit for nothing that needs flat.
 * The storage rack is the one build where the cheap stuff belongs — and
 * it refuses the good sheets, so a rack never eats jig stock by mistake.
 */
export const JIG_GRADE_KINDS: ReadonlyArray<SheetGoodKind> = [
  "plywoodA",
  "plywoodB",
  "plywoodC",
  "mdf",
];
export const SHOP_FURNITURE_KINDS: ReadonlyArray<SheetGoodKind> = [
  ...JIG_GRADE_KINDS,
  "particleBoard",
];
export const RACK_GRADE_KINDS: ReadonlyArray<SheetGoodKind> = [
  "osb",
  "particleBoard",
  "plywoodC",
];

/**
 * Where a piece's face lies on the virgin sheet it was cut from —
 * cosmetic provenance for the renderer, so the two pieces a cut makes
 * keep wearing adjacent stretches of the same veneer instead of each
 * rerolling its look. Purely visual: grain direction still never gates
 * a cut or a recipe (see docs/sheet-goods.md).
 *
 * `u` runs along the source sheet's length (the veneer's grain), `v`
 * across its width, both inches from the piece's origin corner.
 * `rotated` records that the piece's own length/width axes sit swapped
 * against the source's — the mark `makeSheet` leaves when normalizing a
 * piece whose long side came off the cut the other way round. The
 * renderer treats the swap as a transpose rather than a true quarter
 * turn; on woodgrain the mirror is undetectable and the bookkeeping
 * stays two numbers and a flag.
 */
export interface SheetFaceRegion {
  /** Identity of the virgin source sheet — seeds where on the (tiling)
   * face art that sheet's window sits, so different sheets of one kind
   * don't all show the same stretch of veneer. */
  readonly seed: string;
  readonly u: number;
  readonly v: number;
  readonly rotated: boolean;
}

export interface SheetGood {
  readonly id: string;
  readonly type: "plywood";
  /** Inches — a full sheet is 96 × 48. */
  readonly length: number;
  readonly width: number;
  readonly thickness: SheetThickness;
  readonly kind: SheetGoodKind;
  /** Absent on a virgin sheet — the piece IS its own source, region
   * (0,0) unrotated (see sheetFaceRegion). Cuts fill it in. */
  readonly face?: SheetFaceRegion;
}

/** One strip of wood in a glued-up panel. */
export interface PanelStrip {
  readonly species: Species;
  readonly width: BoardDimension;
}

/**
 * A glued-up panel: an ordered list of strips sharing one length and
 * thickness. Total width is derived (see panelWidth), so it can exceed the
 * largest stock board dimension — that's the point of gluing. The strip list
 * carries species per strip, so multi-species patterns price and render
 * correctly with no extra machinery.
 */
export interface Panel {
  readonly id: string;
  readonly type: "panel";
  /** Inches, like a board's. */
  readonly length: number;
  readonly thickness: BoardDimension;
  readonly strips: ReadonlyArray<PanelStrip>;
  readonly surface: SurfaceCondition;
  /**
   * Which way the fibers face. "end" panels (crosscut slices re-glued
   * grain-up) can never be planed — a planer tears end grain apart — so
   * sanding is the only way to flatten them. Absent means "long".
   */
  readonly grain?: "long" | "end";
}

/**
 * One crosscut slice of a long-grain panel, destined to be stood on end
 * and glued into an end-grain panel. Carries the source panel's strip
 * pattern — that's what makes checkerboards possible later.
 */
export interface EndGrainSlice {
  readonly id: string;
  readonly type: "endGrainSlice";
  readonly strips: ReadonlyArray<PanelStrip>;
  /** Thickness of the source panel — the slice's glue-face width. */
  readonly thickness: BoardDimension;
}

/** Total width is derived from the strips — never stored. */
export function panelWidth(panel: Panel): number {
  return panel.strips.reduce((sum, strip) => sum + strip.width, 0);
}

/** The distinct species in a panel, in first-appearance order. */
export function panelSpecies(panel: Panel): ReadonlyArray<Species> {
  return [...new Set(panel.strips.map((strip) => strip.species))];
}

/**
 * Applied finishes. Mineral oil is the food-safe one — the only finish a
 * cutting board takes. Film finishes (wax oil, lacquer, poly) join this
 * union when the wider finishing system lands.
 */
export const FINISHES = ["mineralOil"] as const;
export type Finish = (typeof FINISHES)[number];

/**
 * The bench footprint of each machine-made product, in inches — the one
 * declaration both the sprite's frame (drawCuttingBoard) and the bench's
 * hit box (pieceSize) draw from, so a piece is grabbable exactly where
 * it shows. Blueprint-assembled products don't appear here: their
 * blueprint's box is their footprint, by the same rule.
 */
export const CUTTING_BOARD_FOOTPRINTS = {
  simpleCuttingBoard: { widthIn: 10, heightIn: 16 },
  stripedCuttingBoard: { widthIn: 10, heightIn: 16 },
  sunriseCuttingBoard: { widthIn: 12, heightIn: 16 },
  endGrainCuttingBoard: { widthIn: 10, heightIn: 16 },
  checkerboardCuttingBoard: { widthIn: 10, heightIn: 16 },
} as const satisfies Record<string, { widthIn: number; heightIn: number }>;

export type CuttingBoardType = keyof typeof CUTTING_BOARD_FOOTPRINTS;

export const CUTTING_BOARD_TYPES = Object.keys(
  CUTTING_BOARD_FOOTPRINTS,
) as ReadonlyArray<CuttingBoardType>;

/**
 * Every sellable finished-product type: the machine-made cutting boards
 * above, plus every product the plan drawer builds (one JSON record
 * apiece in `src/game/plans/`). The FinishedProduct union,
 * isFinishedProduct, pricing, and mock builders all derive from this
 * list — a new plan record is a new product everywhere at once.
 */
export type FinishedProductType = CuttingBoardType | ProductPlanId;

export const FINISHED_PRODUCT_TYPES: ReadonlyArray<FinishedProductType> = [
  ...CUTTING_BOARD_TYPES,
  ...PRODUCT_PLAN_IDS,
];

/**
 * One board in an assembled product's bill of materials: which blueprint
 * slot it fills, the stock it was (Board units: width in inches, length
 * in inches, thickness in quarters), and the grain seed it keeps — the
 * consumed board's own id, so the very grain that lay on the bench is
 * the grain in the finished piece.
 */
export type AssembledPart = {
  readonly slot: string;
  readonly species: Species;
  /** Total width in inches — a board's catalog width, or a panel part's
   * derived strip sum (which outgrows the board catalog). */
  readonly width: number;
  readonly length: number;
  readonly thickness: BoardDimension;
  /** Present when the part is a glued-up panel (a tray's bottom): the
   * very strips that went in, so the finished piece shows the glue-up's
   * stripes. Absent means the part is a plain board. */
  readonly strips?: ReadonlyArray<PanelStrip>;
  /** The board's surface as it went in — sanded stock draws sanded in
   * the finished piece. Absent (older saves) means rough. */
  readonly surface?: SurfaceCondition;
  /** The board's end treatments as it lies in its slot — a frame rail's
   * mirrored miters are what close the corners, so the finished piece
   * keeps them. Absent (older saves, square stock) means square. */
  readonly ends?: BoardEnds;
  /** A sheet-good part (a jig's base, a table's top) keeps its kind —
   * the veneer it shows is the sheet's, not a species'. */
  readonly sheetKind?: SheetGoodKind;
  readonly seed: string;
};

export type FinishedProduct = {
  readonly id: string;
  readonly type: FinishedProductType;
  readonly species: Species;
  /** Second wood in a two-tone piece (e.g. striped cutting boards). */
  readonly accentSpecies?: Species;
  /** Absent means raw wood — finishing is a separate, value-adding step. */
  readonly finish?: Finish;
  /**
   * The parts a blueprint-assembled product is built from (see
   * bench-work/blueprint.ts). Absent on products from before blueprints
   * (and on non-assembled products); renderers fall back to the
   * blueprint's nominal parts.
   */
  readonly parts?: ReadonlyArray<AssembledPart>;
};

/** One nail in a pallet, at the crossing of a deck board and a stringer
 * — a nail always joins exactly those two boards. */
export type PalletNail = {
  readonly deck: number;
  readonly stringer: number;
};

export type Pallet = {
  readonly id: string;
  readonly type: "pallet";
  /** Which deck boards still hold on, left to right — three bottom then
   * five top (see bench-work/pallet-geometry). Every one of them is the
   * same board as every stringer; the two names are directions, not
   * sizes. */
  readonly deckBoards: Tuple<boolean, 8>;
  /** Which stringers still hold on, top to bottom — per board, like the
   * deck. A board comes free when its last nail is pried. */
  readonly stringers: Tuple<boolean, 3>;
  /** Every nail still driven, one per crossing of two present boards.
   * Prying is per-nail (pryPalletNailAction); these render in both the
   * shop view and the bench view (PalletSprite). */
  readonly nails: ReadonlyArray<PalletNail>;
};

/**
 * A handheld tool as a physical object: in the truck's bed on the ride
 * home, in the arms, in a pile on the floor, or on a station's shelf.
 * It stops being a material when mounted — a station's rack holds plain
 * ToolIds (MachineState.tools), and unmounting mints a fresh instance.
 */
export type ToolItem = {
  readonly id: string;
  readonly type: "tool";
  readonly toolId: ToolId;
};

export type UnknownMaterial = {
  readonly id: string;
  readonly type: "unknown";
};

export type MaterialInstance =
  | Pallet
  | Board
  | SheetGood
  | Panel
  | EndGrainSlice
  | FinishedProduct
  | ToolItem
  | UnknownMaterial;

export type MaterialType = MaterialInstance["type"];
